import React, { useState, useEffect } from 'react';
import { ProtocolBlock, ProtocolField } from '../../../types/protocols';
import './ProtocolBlockBuilder.scss';
import CustomSelect from '../../../components/CustomSelect';

// Import icons
import textIcon from '../../../../assets/field-icons/text-icon.svg';
import numberIcon from '../../../../assets/field-icons/number-icon.svg';
import selectIcon from '../../../../assets/field-icons/select-icon.svg';
import checkboxIcon from '../../../../assets/field-icons/checkbox-icon.svg';
import dateIcon from '../../../../assets/field-icons/date-icon.svg';
import plusIcon from '../../../../assets/field-icons/plus-icon.svg';
import trashIcon from '../../../../assets/field-icons/trash-icon.svg';
import dragIcon from '../../../../assets/field-icons/drag-icon.svg';

interface ProtocolBlockBuilderProps {
  onBlocksChange: (blocks: ProtocolBlock[]) => void;
  onJsonChange: (json: string) => void;
  specialty?: string;
  instructions?: string;
  existingJsonProtocol?: string;
}

const ProtocolBlockBuilder: React.FC<ProtocolBlockBuilderProps> = ({
  onBlocksChange,
  onJsonChange,
  specialty = "",
  instructions = "",
  existingJsonProtocol = "",
}) => {
  const [blocks, setBlocks] = useState<ProtocolBlock[]>([
    {
      id: '1',
      title: 'Основная информация',
      fields: [],
      order: 1,
    },
  ]);

  // Логируем изменения в blocks
  useEffect(() => {
    console.log('ProtocolBlockBuilder - blocks changed:', blocks);
  }, [blocks]);

  const [nextBlockId, setNextBlockId] = useState(2);
  const [draggedBlock, setDraggedBlock] = useState<string | null>(null);
  const [dragOverBlock, setDragOverBlock] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);

  // Parse existing JSON protocol on component mount only once
  useEffect(() => {
    if (existingJsonProtocol && existingJsonProtocol.trim()) {
      setIsLoadingExisting(true);
      parseExistingProtocol(existingJsonProtocol);
      setIsInitialized(true);
      setIsLoadingExisting(false);
    }
  }, [existingJsonProtocol]);

  // Regenerate JSON when specialty or instructions change
  useEffect(() => {
    // Не генерируем JSON, если мы только что загрузили существующий протокол
    if (isInitialized && !isLoadingExisting) {
      generateJson(blocks);
    }
  }, [specialty, instructions, isInitialized, isLoadingExisting]);

  const parseExistingProtocol = (jsonString: string) => {
    try {
      console.log('Parsing existing protocol:', jsonString);
      const parsedProtocol = JSON.parse(jsonString);

      if (parsedProtocol.fields) {
        const parsedBlocks: ProtocolBlock[] = [];

        // Обрабатываем поля и ищем блоки
        Object.entries(parsedProtocol.fields).forEach(([fieldKey, fieldConfig]: [string, any]) => {
          // Пропускаем системные поля
          if (fieldKey === 'LAZYDOC_NOTES') return;

          // Если это блок (новый формат)
          if (fieldConfig.type === 'block') {
            const blockId = fieldKey;
            const blockFields: ProtocolField[] = [];

            // Обрабатываем поля блока
            if (fieldConfig.fields) {
              Object.entries(fieldConfig.fields).forEach(([subFieldKey, subFieldConfig]: [string, any]) => {
                const field: ProtocolField = {
                  id: Date.now() + Math.random(),
                  name: subFieldKey,
                  label: subFieldConfig.label,
                  type: subFieldConfig.type,
                  description: subFieldConfig.description || '',
                  required: subFieldConfig.required || false,
                  default_value: subFieldConfig.default || '',
                  choices: subFieldConfig.options || subFieldConfig.choices || [],
                  range_min: subFieldConfig.min || (subFieldConfig.range ? subFieldConfig.range[0] : undefined),
                  range_max: subFieldConfig.max || (subFieldConfig.range ? subFieldConfig.range[1] : undefined),
                  protocol_id: 0
                };

                blockFields.push(field);
              });
            }

            const block: ProtocolBlock = {
              id: blockId,
              title: fieldConfig.label,
              fields: blockFields,
              order: fieldConfig.order || parseInt(blockId)
            };

            parsedBlocks.push(block);
          } else {
            // Старый формат - все поля в одном блоке
            const field: ProtocolField = {
              id: Date.now() + Math.random(),
              name: fieldKey,
              label: fieldConfig.label,
              type: fieldConfig.type,
              description: fieldConfig.description || '',
              required: fieldConfig.required || false,
              default_value: fieldConfig.default || '',
              choices: fieldConfig.options || fieldConfig.choices || [],
              range_min: fieldConfig.min || (fieldConfig.range ? fieldConfig.range[0] : undefined),
              range_max: fieldConfig.max || (fieldConfig.range ? fieldConfig.range[1] : undefined),
              protocol_id: 0
            };

            // Ищем существующий блок или создаем новый
            let existingBlock = parsedBlocks.find(b => b.id === '1');
            if (!existingBlock) {
              existingBlock = {
                id: '1',
                title: 'Основная информация',
                fields: [],
                order: 1
              };
              parsedBlocks.push(existingBlock);
            }

            existingBlock.fields.push(field);
          }
        });

        // Сортируем блоки по order
        parsedBlocks.sort((a, b) => a.order - b.order);

        if (parsedBlocks.length > 0) {
          console.log('Parsed blocks:', parsedBlocks);
          setBlocks(parsedBlocks);
          setNextBlockId(Math.max(...parsedBlocks.map(b => parseInt(b.id) || 0)) + 1);

          // Принудительно вызываем onBlocksChange для уведомления родителя
          onBlocksChange(parsedBlocks);
        } else {
          console.warn('No blocks found in protocol');
        }
      }
    } catch (error) {
      console.error('Failed to parse existing protocol JSON:', error);
      // В случае ошибки оставляем значения по умолчанию
    }
  };

  const addBlock = () => {
    const newBlock: ProtocolBlock = {
      id: nextBlockId.toString(),
      title: `Блок ${nextBlockId}`,
      fields: [],
      order: nextBlockId,
    };

    const updatedBlocks = [...blocks, newBlock];
    setBlocks(updatedBlocks);
    setNextBlockId(nextBlockId + 1);
    onBlocksChange(updatedBlocks);
    generateJson(updatedBlocks);
  };

  const removeBlock = (blockId: string) => {
    if (blocks.length <= 1) return;

    const updatedBlocks = blocks.filter(block => block.id !== blockId);
    setBlocks(updatedBlocks);
    onBlocksChange(updatedBlocks);
    generateJson(updatedBlocks);
  };

  const updateBlockTitle = (blockId: string, title: string) => {
    const updatedBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, title } : block
    );
    setBlocks(updatedBlocks);
    onBlocksChange(updatedBlocks);
    generateJson(updatedBlocks);
  };

  const addField = (blockId: string) => {
    const newField: ProtocolField = {
      id: Date.now(),
      name: `FIELD_${Date.now()}`,
      type: 'free-text',
      description: '',
      required: false,
      label: '',
      protocol_id: 0,
    };

    const updatedBlocks = blocks.map(block =>
      block.id === blockId
        ? { ...block, fields: [...block.fields, newField] }
        : block
    );

    setBlocks(updatedBlocks);
    onBlocksChange(updatedBlocks);
    generateJson(updatedBlocks);
  };

  const removeField = (blockId: string, fieldId: number) => {
    const updatedBlocks = blocks.map(block =>
      block.id === blockId
        ? { ...block, fields: block.fields.filter(field => field.id !== fieldId) }
        : block
    );

    setBlocks(updatedBlocks);
    onBlocksChange(updatedBlocks);
    generateJson(updatedBlocks);
  };

  const updateField = (blockId: string, fieldId: number, updates: Partial<ProtocolField>) => {
    const updatedBlocks = blocks.map(block =>
      block.id === blockId
        ? {
          ...block,
          fields: block.fields.map(field =>
            field.id === fieldId ? { ...field, ...updates } : field
          ),
        }
        : block
    );

    setBlocks(updatedBlocks);
    onBlocksChange(updatedBlocks);
    generateJson(updatedBlocks);
  };

  // Drag and Drop functions
  const handleDragStart = (e: React.DragEvent, blockId: string) => {
    setDraggedBlock(blockId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', blockId);
  };

  const handleDragOver = (e: React.DragEvent, blockId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBlock(blockId);
  };

  const handleDragLeave = () => {
    setDragOverBlock(null);
  };

  const handleDrop = (e: React.DragEvent, targetBlockId: string) => {
    e.preventDefault();

    if (!draggedBlock || draggedBlock === targetBlockId) {
      setDraggedBlock(null);
      setDragOverBlock(null);
      return;
    }

    const draggedIndex = blocks.findIndex(block => block.id === draggedBlock);
    const targetIndex = blocks.findIndex(block => block.id === targetBlockId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedBlock(null);
      setDragOverBlock(null);
      return;
    }

    const newBlocks = [...blocks];
    const [draggedBlockItem] = newBlocks.splice(draggedIndex, 1);
    newBlocks.splice(targetIndex, 0, draggedBlockItem);

    // Update order property
    const updatedBlocks = newBlocks.map((block, index) => ({
      ...block,
      order: index + 1
    }));

    setBlocks(updatedBlocks);
    onBlocksChange(updatedBlocks);
    generateJson(updatedBlocks);

    setDraggedBlock(null);
    setDragOverBlock(null);
  };

  const handleDragEnd = () => {
    setDraggedBlock(null);
    setDragOverBlock(null);
  };

  const generateJson = (currentBlocks: ProtocolBlock[]) => {
    // Не генерируем JSON, если мы загружаем существующий протокол
    if (isLoadingExisting) {
      return;
    }

    const jsonStructure: any = {
      meta: {
        specialty: specialty || "общая",
        version: "1.0",
        protocols: ["primary", "follow_up"]
      },
      "x-instructions": instructions || "• Заполняй все поля согласно объективному осмотру\n• Используй валидный JSON формат\n• Обязательно заполняй required поля",
      fields: {}
    };

    // Добавляем блоки как поля с типом "block"
    currentBlocks.forEach(block => {
      const blockKey = block.id;
      const blockFields: any = {};

      // Добавляем поля блока
      block.fields.forEach(field => {
        const fieldConfig: any = {
          label: field.label,
          type: field.type,
          required: field.required
        };

        if (field.description) {
          fieldConfig.description = field.description;
        }

        if (field.default_value) {
          fieldConfig.default = field.default_value;
        }

        if (field.choices && field.choices.length > 0) {
          fieldConfig.options = field.choices;
        }

        if (field.type === 'number') {
          if (field.range_min !== undefined) {
            fieldConfig.min = field.range_min;
          }
          if (field.range_max !== undefined) {
            fieldConfig.max = field.range_max;
          }
        }

        blockFields[field.name] = fieldConfig;
      });

      // Создаем блок
      jsonStructure.fields[blockKey] = {
        label: block.title,
        type: "block",
        order: block.order,
        fields: blockFields
      };
    });

    // Добавляем обязательное поле LAZYDOC_NOTES
    jsonStructure.fields.LAZYDOC_NOTES = {
      label: "ПРИМЕЧАНИЯ ОТ LazyDOC",
      type: "free-text",
      required: true
    };

    const jsonString = JSON.stringify(jsonStructure, null, 2);
    console.log('ProtocolBlockBuilder - generating JSON:', jsonString);
    onJsonChange(jsonString);
  };

  const fieldTypes = [
    { value: 'free-text', label: 'Текст', icon: textIcon, color: '#3498db' },
    { value: 'number', label: 'Число', icon: numberIcon, color: '#e74c3c' },
    { value: 'select', label: 'Выбор', icon: selectIcon, color: '#9b59b6' },
    { value: 'checkbox', label: 'Чекбокс', icon: checkboxIcon, color: '#27ae60' },
    { value: 'date', label: 'Дата', icon: dateIcon, color: '#f39c12' },
  ];

  return (
    <div className="protocol-block-builder">
      <div className="builder-header">
        <div className="header-left">
          <h2>Конструктор шаблона протоколов</h2>
          <p className="section-description">
            Создайте структуру протокола, добавляя блоки и поля. JSON будет сгенерирован автоматически.
          </p>
        </div>
        <button
          type="button"
          className="button-primary"
          onClick={addBlock}
        >
          Добавить блок
        </button>
      </div>

      <div className="blocks-container">
        {blocks.map((block) => (
          <div
            key={block.id}
            className={`protocol-block ${draggedBlock === block.id ? 'dragging' : ''} ${dragOverBlock === block.id ? 'drag-over' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, block.id)}
            onDragOver={(e) => handleDragOver(e, block.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, block.id)}
            onDragEnd={handleDragEnd}
          >
            <div className="block-header">
              <div className="block-title-container">
                <input
                  type="text"
                  className="block-title-input"
                  value={block.title}
                  onChange={(e) => updateBlockTitle(block.id, e.target.value)}
                  placeholder="Название блока"
                />
                <div className="block-info">
                  <span className="field-count">{block.fields.length} полей</span>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm delete-block-btn"
                onClick={() => removeBlock(block.id)}
                disabled={blocks.length <= 1}
                title="Удалить блок"
              >
                <img src={trashIcon} alt="Delete" className="btn-icon" />
              </button>
            </div>

            <div className="block-fields">
              {block.fields.map((field) => {
                const fieldType = fieldTypes.find(type => type.value === field.type);
                return (
                  <div key={field.id} className="field-item">
                    <div className="field-header">
                      <div className="field-type-indicator">
                        <img
                          src={fieldType?.icon || textIcon}
                          alt={fieldType?.label || 'Text'}
                          className="field-type-icon"
                          style={{ color: fieldType?.color || '#3498db' }}
                        />
                      </div>
                      <input
                        type="text"
                        className="field-name-input"
                        value={field.label}
                        onChange={(e) => updateField(block.id, field.id, { label: e.target.value })}
                        placeholder="Название поля"
                      />
                      <div className="field-actions">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm field-delete-btn"
                          onClick={() => removeField(block.id, field.id)}
                          title="Удалить поле"
                        >
                          <img src={trashIcon} alt="Delete" className="btn-icon" />
                        </button>
                      </div>
                    </div>

                    <div className="field-config">
                      <div className="field-row">
                        <div className="field-group">
                          <label className="field-label">Тип поля</label>
                          <div className="field-type-select-container">
                            <CustomSelect
                              value={field.type}
                              onChange={(value) => updateField(block.id, field.id, { type: value as any })}
                              className="small"
                              placeholder="Тип поля"
                              options={fieldTypes.map(type => ({
                                value: type.value,
                                label: type.label
                              }))}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="field-row">
                        <div className="field-group">
                          <label className="field-label">Описание поля</label>
                          <input
                            type="text"
                            className="field-description-input"
                            value={field.description || ''}
                            onChange={(e) => updateField(block.id, field.id, { description: e.target.value })}
                            placeholder="Описание поля"
                          />
                        </div>
                        <label className="field-required">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(block.id, field.id, { required: e.target.checked })}
                          />
                          Обязательное
                        </label>
                      </div>

                      {field.type === 'select' && (
                        <div className="field-row">
                          <div className="field-group">
                            <label className="field-label">Варианты выбора</label>
                            <input
                              type="text"
                              className="field-choices-input"
                              value={field.choices?.join(', ') || ''}
                              onChange={(e) => {
                                const choices = e.target.value.split(',').map(c => c.trim()).filter(Boolean);
                                updateField(block.id, field.id, { choices });
                              }}
                              placeholder="Варианты выбора через запятую"
                            />
                          </div>
                        </div>
                      )}

                      {field.type === 'number' && (
                        <div className="field-row">
                          <div className="field-group">
                            <label className="field-label">Минимальное значение</label>
                            <input
                              type="number"
                              className="field-range-input"
                              value={field.range_min || ''}
                              onChange={(e) => updateField(block.id, field.id, { range_min: Number(e.target.value) })}
                              placeholder="Мин. значение"
                            />
                          </div>
                          <div className="field-group">
                            <label className="field-label">Максимальное значение</label>
                            <input
                              type="number"
                              className="field-range-input"
                              value={field.range_max || ''}
                              onChange={(e) => updateField(block.id, field.id, { range_max: Number(e.target.value) })}
                              placeholder="Макс. значение"
                            />
                          </div>
                        </div>
                      )}

                      <div className="field-row">
                        <div className="field-group">
                          <label className="field-label">Значение по умолчанию</label>
                          <input
                            type="text"
                            className="field-default-input"
                            value={field.default_value || ''}
                            onChange={(e) => updateField(block.id, field.id, { default_value: e.target.value })}
                            placeholder="Значение по умолчанию"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                className="btn btn-outline-primary btn-sm add-field-btn"
                onClick={() => addField(block.id)}
              >
                <img src={plusIcon} alt="Add" className="btn-icon" />
                Добавить поле
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProtocolBlockBuilder;
